import { DateTime } from 'luxon';
import { DB_FIELD_LABELS } from './importFields';

const IDENTIFIERS = new Set(['equipment_tag', 'old_rfid', 'model_no', 'serial_no', 'certificate_number', 'work_order']);
export const IMPORT_ROW_NUMBER = Symbol('importRowNumber');
export const getImportRowNumber = (row, index) => row[IMPORT_ROW_NUMBER] || index + 2;

export const parseImportDate = (value) => {
  if (value === null || value === undefined || String(value).trim() === '') return null;
  if (value instanceof Date) {
    const date = DateTime.fromJSDate(value, { zone: 'utc' });
    if (date.isValid && date.year >= 1900 && date.year <= 9999) return date.toISODate();
    throw new Error('Enter a valid date as YYYY-MM-DD.');
  }
  const raw = String(value).trim();
  const formats = ['yyyy-MM-dd', 'd/M/yyyy', 'M/d/yyyy', 'd.M.yyyy', 'd-MMM-yyyy', 'd-MMM-yy'];
  const dates = new Set(formats.map(format => DateTime.fromFormat(raw, format, { zone: 'utc', locale: 'en' }))
    .filter(date => date.isValid && date.year >= 1900 && date.year <= 9999)
    .map(date => date.toISODate()));
  if (dates.size > 1) throw new Error('Ambiguous date. Use YYYY-MM-DD to specify the month and day.');
  if (!dates.size) throw new Error('Invalid date. Use YYYY-MM-DD, for example 2026-10-22.');
  return [...dates][0];
};

export const normalizeImportValue = (field, value) => {
  if (field === 'inspection_date') return parseImportDate(value);
  if (value instanceof Date) throw new Error('This cell was converted to a date. Re-enter the original value in a Text-formatted cell.');
  if (typeof value === 'number' && (!Number.isFinite(value) || Math.abs(value) >= 1e15)) {
    throw new Error('Numeric value may have lost digits in Excel. Re-enter the original value as Text.');
  }
  const text = String(value ?? '').trim();
  if (IDENTIFIERS.has(field) && /^[’']/.test(text)) {
    throw new Error('Identifier contains a leading apostrophe. Check the original value and remove the literal apostrophe before importing.');
  }
  if (IDENTIFIERS.has(field) && /^[+-]?\d+(?:\.\d+)?e[+-]\d+$/i.test(text)) {
    throw new Error('Identifier looks like scientific notation. Re-enter the original identifier as Text.');
  }
  return text || null;
};

// Validate the whole file before any writes, retaining identifiers exactly as
// entered. Leading zeros can be meaningful and must never be added or removed.
export const prepareImportRows = (rows, mapping, availableFields) => {
  const errors = [];
  const allowedMapping = Object.entries(mapping).filter(([field, column]) => availableFields.includes(field) && column);
  const skipReasons = [];
  const firstRowByTag = new Map();
  const values = rows.map((row, index) => {
    const item = {};
    const tagColumn = mapping.equipment_tag;
    if (tagColumn) {
      try {
        const tag = normalizeImportValue('equipment_tag', row[tagColumn]);
        if (!tag) {
          skipReasons[index] = 'Missing mandatory equipment tag.';
          return item;
        }
        if (firstRowByTag.has(tag)) {
          skipReasons[index] = `Duplicate equipment tag "${tag}" (first appears in row ${firstRowByTag.get(tag)}).`;
          return item;
        }
        firstRowByTag.set(tag, getImportRowNumber(row, index));
      } catch {
        // Report invalid tag values through the normal field validation below.
      }
    }
    allowedMapping.forEach(([field, column]) => {
      try {
        item[field] = normalizeImportValue(field, row[column]);
      } catch (error) {
        errors.push(`Row ${getImportRowNumber(row, index)}, ${DB_FIELD_LABELS[field]}: ${error.message}`);
      }
    });
    return item;
  });
  return { values, errors, skipReasons };
};
